if (!customElements.get('pickup-availability')) {
  /**
   * Custom element that handles fetching and displaying store pickup availability for product variants.
   * @extends HTMLElement
   */
  customElements.define(
    'pickup-availability',
    class PickupAvailability extends HTMLElement {
      constructor() {
        super();

        if (!this.hasAttribute('available')) return;

        this.errorHtml = this.querySelector('template').content.firstElementChild.cloneNode(true);
        this.onClickRefreshList = this.onClickRefreshList.bind(this);
        this.fetchAvailability(this.dataset.variantId);
      }

      /**
       * Fetches pickup availability HTML for a given variant ID via AJAX.
       * @param {string} variantId
       */
      fetchAvailability(variantId) {
        if (!variantId) return;

        let rootUrl = this.dataset.rootUrl;
        if (!rootUrl.endsWith('/')) {
          rootUrl = rootUrl + '/';
        }
        const variantSectionUrl = `${rootUrl}variants/${variantId}/?section_id=pickup-availability`;

        fetch(variantSectionUrl)
          .then((response) => response.text())
          .then((text) => {
            const sectionInnerHTML = new DOMParser()
              .parseFromString(text, 'text/html')
              .querySelector('.shopify-section');
            this.renderPreview(sectionInnerHTML);
          })
          .catch((e) => {
            const button = this.querySelector('button');
            if (button) button.removeEventListener('click', this.onClickRefreshList);
            this.renderError();
          });
      }

      onClickRefreshList() {
        this.fetchAvailability(this.dataset.variantId);
      }

      /**
       * Updates pickup availability when selected variant changes.
       * @param {Object} [variant]
       * @param {string|number} variant.id
       * @param {boolean} variant.available
       */
      update(variant) {
        if (variant?.available) {
          this.fetchAvailability(variant.id);
        } else {
          this.removeAttribute('available');
          this.innerHTML = '';
        }
      }

      renderError() {
        this.innerHTML = '';
        this.appendChild(this.errorHtml);

        this.querySelector('button').addEventListener('click', this.onClickRefreshList);
      }

      /**
       * Renders the pickup preview and injects the drawer component into the DOM.
       * @param {Element} sectionInnerHTML
       */
      renderPreview(sectionInnerHTML) {
        const drawer = document.querySelector('pickup-availability-drawer');
        if (drawer) drawer.remove();
        if (!sectionInnerHTML.querySelector('pickup-availability-preview')) {
          this.innerHTML = '';
          this.removeAttribute('available');
          return;
        }

        this.innerHTML = sectionInnerHTML.querySelector('pickup-availability-preview').outerHTML;
        this.setAttribute('available', '');

        document.body.appendChild(sectionInnerHTML.querySelector('pickup-availability-drawer'));
        const colorClassesToApply = this.dataset.productPageColorScheme.split(' ');
        colorClassesToApply.forEach((colorClass) => {
          document.querySelector('pickup-availability-drawer').classList.add(colorClass);
        });

        const button = this.querySelector('button');
        if (button)
          button.addEventListener('click', (evt) => {
            document.querySelector('pickup-availability-drawer').show(evt.target);
          });
      }
    }
  );
}

if (!customElements.get('pickup-availability-drawer')) {
  /**
   * Custom element representing the pickup availability drawer modal.
   * @extends HTMLElement
   */
  customElements.define(
    'pickup-availability-drawer',
    class PickupAvailabilityDrawer extends HTMLElement {
      constructor() {
        super();

        this.onBodyClick = this.handleBodyClick.bind(this);

        this.querySelector('button').addEventListener('click', () => {
          this.close();
        });

        this.addEventListener('keyup', (event) => {
          if (event.code.toUpperCase() === 'ESCAPE') this.close();
        });
      }

      handleBodyClick(evt) {
        const target = evt.target;
        if (
          target != this &&
          !target.closest('pickup-availability-drawer') &&
          target.id != 'ShowPickupAvailabilityDrawer'
        ) {
          this.close();
        }
      }

      /** Closes the drawer and releases focus trap. */
      close() {
        this.classList.remove('animate');
        setTimeout(() => {
          this.classList.remove('active');
          document.body.classList.remove('overflow-hidden');
          document.body.style.paddingRight = '';
        }, 500);
        removeTrapFocus(this.focusElement);
      }

      /**
       * Opens the drawer and sets focus to the trigger element.
       * @param {HTMLElement} focusElement
       */
      show(focusElement) {
        setTimeout(() => {
          this.classList.add('active');
          this.classList.add('animate');
        });
        document.body.style.paddingRight = this.getScrollbarWidth() + 'px';
        document.body.classList.add('overflow-hidden');
        
        this.focusElement = focusElement;
        trapFocus(this);
      }

      /**
       * Calculates the layout scrollbar width in pixels.
       * @returns {number}
       */
      getScrollbarWidth() {
        return window.innerWidth - document.documentElement.clientWidth;
      }
    }
  );
}